import { CircularProgress, Divider, Typography } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import { DeliveryMode } from "api/resources/learningItems/types"
import { OrderItemType } from "api/resources/orders/types"
import clsx from "clsx"
import ErrorPage from "components/ErrorPage"
import FullPageLoading from "components/FullPageLoading"
import ForYou from "features/LearningItems/ForYou"
import { useState } from "react"
import { getRemoteMedia } from "utils/getRemoteMedia"
import { useEnrollments } from "utils/hooks/useEnrollments"

import { useQualificationDetails } from "../Qualification"
import ActivityBreakdownCard from "./ActivityBreakdownCard"
import AssignmentsAnalytics from "./AssignmentsAnalytics"
import ModuleProgressBar from "./ModuleProgressBar"
import OverallGPA from "./OverallGPA"
import ProgressTracker from "./ProgressTracker"
import SegmentedFilterButtons from "./SegmentedFilterButtons"
import TimeSpent from "./TimeSpent"
import { TimeInterval, timeIntervalOptions } from "./types"
import {
  getAssessmentProgressBarData,
  mapActivityData,
  renderAnalyticsMetric,
  useTypeAndSlug,
} from "./utils"

export { TimeInterval }

const defaultTermValue = { label: "Term", value: "" }
const defaultCourseValue = { label: "Modules", value: "" }

const LearnerAnalytics = () => {
  const [filteredTerm, setFilteredTerm] = useState(defaultTermValue)
  const [filteredCourse, setFilteredCourse] = useState(defaultCourseValue)

  const [selectedTimeInterval, setSelectedTimeInterval] = useState<{
    value: TimeInterval
    label: string
  }>(timeIntervalOptions[0])
  const { type, slug } = useTypeAndSlug()
  const { enrollments } = useEnrollments()
  const { qualification } = useQualificationDetails()

  const qualificationId = qualification?.id ?? 0
  const isCourse = type === OrderItemType.COURSE || !!filteredCourse.value
  const matchedEnrollment = enrollments?.find(enr => enr.slug === slug)
  const learningItemId = filteredCourse.value
    ? Number(filteredCourse.value)
    : matchedEnrollment?.id || ""
  const learningItemType = isCourse
    ? OrderItemType.COURSE
    : OrderItemType.QUALIFICATION
  const term = filteredTerm.value || undefined

  const {
    data: analyticsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: queries.analytics.getLearnerAnalytics(
      learningItemId,
      learningItemType,
      term
    ).queryKey,
    queryFn: () =>
      api.analytics.getLearnerAnalytics({
        urlParams: {
          learningItemId,
          learningItemType,
        },
        params: term ? { term } : {},
      }),
  })

  const timeSpentQuery = useQuery({
    queryKey: queries.analytics.getTimeSpentAnalyticsQueryKey({
      itemId: learningItemId,
      itemType: learningItemType,
      interval: selectedTimeInterval.value,
      ...(term && { term }),
    }).queryKey,
    queryFn: () =>
      api.analytics.getTimeSpentAnalytics({
        urlParams: {
          itemId: learningItemId,
          itemType: learningItemType,
        },
        params: {
          interval: selectedTimeInterval.value,
          ...(term ? { term } : {}),
        },
      }),
  })

  const { data: plannerList } = useQuery({
    enabled: Boolean(qualificationId),
    queryFn: () =>
      api.learningItems.getPlannerList({
        params: {
          learningItemType: learningItemType,
          learningItemId: qualificationId,
        },
      }),
    queryKey: queries.learningItems.listPlanners(qualificationId).queryKey,
  })

  const sortedTerms = Array.isArray(analyticsData?.terms)
    ? [...analyticsData.terms].sort((a, b) => Number(a.name) - Number(b.name))
    : []

  const termMapOptions = sortedTerms.map(term => ({
    label: `Term ${term.name}`,
    value: term.name.toString(),
    isDisabled: !term.unlocked,
  }))

  const moduleOptions =
    plannerList?.map(module => ({
      label: module.name,
      value: `${module.id}`,
      isDisabled:
        module.progress === null ||
        module.progress.completedUserPlannerItems === 0,
    })) || []

  const selectedModule = plannerList?.find(
    module => module.id === Number(filteredCourse.value)
  )

  const deliveryMode = analyticsData?.deliveryMode || DeliveryMode.SELF_PACED

  const activities = mapActivityData({ data: analyticsData, deliveryMode })

  // Handler functions for the segmented buttons
  const handleOverallClick = () => {
    setFilteredTerm(defaultTermValue)
    setFilteredCourse(defaultCourseValue)
  }

  const handleTermSelect = (option: { label: string; value: string }) => {
    setFilteredCourse(defaultCourseValue)
    setFilteredTerm(option)
  }

  const handleTermClear = () => {
    setFilteredTerm(defaultTermValue)
  }

  const handleModuleSelect = (option: { label: string; value: string }) => {
    setFilteredTerm(defaultTermValue)
    setFilteredCourse(option)
  }

  const handleModuleClear = () => {
    setFilteredCourse(defaultCourseValue)
  }

  if (isLoading) return <FullPageLoading />

  return (
    <div className="mb-3 grid grid-cols-1 gap-3 lg:grid-cols-12 lg:gap-x-6">
      <div className="flex flex-col gap-2 lg:col-span-8">
        <div className="flex flex-col gap-1 sm:flex-row">
          {type === OrderItemType.QUALIFICATION && (
            <SegmentedFilterButtons
              onOverallClick={handleOverallClick}
              termOptions={termMapOptions}
              selectedTerm={filteredTerm}
              onTermSelect={handleTermSelect}
              onTermClear={handleTermClear}
              moduleOptions={moduleOptions}
              selectedModule={filteredCourse}
              onModuleSelect={handleModuleSelect}
              onModuleClear={handleModuleClear}
              showModules={moduleOptions && moduleOptions.length > 0}
            />
          )}
        </div>

        {filteredCourse.value && selectedModule && (
          <div className="my-1.25 flex flex-col gap-2 sm:flex-row sm:items-center">
            <img
              src={getRemoteMedia(
                `product-thumbnails/${
                  selectedModule.slug || "/placeholder.svg"
                }`,
                {
                  width: 56,
                }
              )}
              alt="course logo"
              className="size-7 rounded-lg object-cover"
            />
            <div>
              <Typography
                variant="preTitle"
                className="mb-1 text-onSurface-400"
              >
                MODULE
              </Typography>
              <Typography variant="title3" className="text-onSurface-800">
                {selectedModule.name}
              </Typography>
            </div>
          </div>
        )}

        <div className="flex flex-col rounded-2xl border border-onSurface-200 sm:flex-row">
          <div className="w-full sm:w-2/3">
            <ProgressTracker
              progress={analyticsData?.progress}
              terms={sortedTerms}
              filteredTerm={filteredTerm.value}
              filteredModule={filteredCourse.value}
              isCourse={isCourse}
            />
          </div>

          <div className="hidden gap-1.25 sm:flex">
            <Divider orientation="vertical" className="bg-onSurface-200" />
          </div>
          <Divider className="block bg-onSurface-200 sm:hidden" />
          <div className="w-full p-2.5 sm:w-1/2">
            <TimeSpent
              weeklyTimeSpentData={timeSpentQuery.data?.intervals?.map(
                interval => interval.totalTime
              )}
              isCourse={isCourse}
              totalTimeSpent={timeSpentQuery.data?.totalTimeSpent}
              weeklyTimeSpent={timeSpentQuery.data?.avgWeeklyTimeSpent}
              selectedTimeInterval={selectedTimeInterval}
              setSelectedTimeInterval={setSelectedTimeInterval}
              timeIntervalOptions={timeIntervalOptions}
              timeGraphLoading={timeSpentQuery.isLoading}
              thisWeekTimeSpent={
                timeSpentQuery.data?.intervals &&
                timeSpentQuery.data?.intervals[0]?.totalTime
              }
            />
          </div>
        </div>

        {/* section 2 */}
        <div className="flex flex-col rounded-2xl border border-onSurface-200 sm:flex-row">
          <div className="w-full sm:w-1/2">
            <OverallGPA
              gpa={analyticsData?.gpa}
              isCourse={isCourse}
              filteredCourseSlug={selectedModule?.slug}
            />
          </div>
          <div className="hidden gap-1.25 sm:flex">
            <Divider orientation="vertical" className="bg-onSurface-200" />
          </div>
          <Divider className="block bg-onSurface-200 sm:hidden" />
          <div className="flex w-full flex-col justify-between gap-4 p-2.5 sm:w-1/2 sm:gap-6">
            <Typography className="text-muted" variant="strongSmallBody">
              Total Credits Earned
            </Typography>
            {renderAnalyticsMetric(
              analyticsData?.creditsEarned,
              ({ isNum, formattedValue }) => (
                <Typography
                  variant="title1"
                  className={clsx("text-onSurface-900", {
                    "!font-thin text-onSurface-400": !isNum,
                  })}
                >
                  {formattedValue}
                </Typography>
              )
            )}
          </div>
        </div>

        {isError ? (
          <ErrorPage />
        ) : (
          <>
            {!isCourse && (
              <Typography className="mt-2 text-onSurface-700" variant="title3">
                Modules
              </Typography>
            )}
            <div className="flex flex-col rounded-2xl border border-onSurface-200 md:flex-row">
              {!isCourse && (
                <>
                  <div className="w-full md:w-1/2">
                    <ModuleProgressBar
                      title="Progress"
                      completed={analyticsData?.modules?.passed || 0}
                      totalProgress={analyticsData?.modules?.total || 0}
                      items={[
                        {
                          label: "Passed",
                          value: analyticsData?.modules?.passed || 0,
                          color: "bg-success-500",
                        },
                        {
                          label: "Failed",
                          value: analyticsData?.modules?.failed || 0,
                          color: "bg-critical-500",
                        },
                      ]}
                    />
                  </div>
                  <div className="hidden gap-1.25 md:flex">
                    <Divider
                      orientation="vertical"
                      className="bg-onSurface-200"
                    />
                  </div>
                  <Divider className="block bg-onSurface-200 md:hidden" />
                </>
              )}
              <div
                className={clsx("w-full md:w-1/2", { "md:w-full": isCourse })}
              >
                <ActivityBreakdownCard
                  isCourse={isCourse}
                  activities={activities}
                />
              </div>
            </div>

            {/* section 4 */}
            <div className="flex flex-col rounded-2xl border border-onSurface-200 sm:flex-row">
              <div className="w-full sm:w-1/2">
                <ModuleProgressBar
                  {...getAssessmentProgressBarData({
                    analyticsData,
                    deliveryMode,
                  })}
                />
              </div>
              <div className="hidden gap-1.25 sm:flex">
                <Divider orientation="vertical" className="bg-onSurface-200" />
              </div>
              <Divider className="block bg-onSurface-200 sm:hidden" />
              <div className="w-full sm:w-1/2">
                <AssignmentsAnalytics
                  assignments={analyticsData?.assignments}
                  classes={{
                    liveClasses: analyticsData?.liveClasses,
                    recordedClasses: analyticsData?.recordedClasses,
                  }}
                  deliveryMode={deliveryMode}
                />
              </div>
            </div>
          </>
        )}
        {/* section 3 */}
      </div>

      <div className="flex flex-col gap-4 lg:col-span-4">
        <ForYou />
      </div>
    </div>
  )
}

export default LearnerAnalytics
